import * as React from "react";
import {useState} from "react";

// import { createStyles, Theme } from '@mui/material/styles';
// import { makeStyles } from '@mui/styles';
import {Box, Button, Checkbox, TableCell, TableRow, Theme, Tooltip, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {TableLoading, TableLoadingSkeleton, TableWithError, TableWithoutData} from './TableLayoutsState';

import {dateFormatter} from 'src/utils/formatters/dateFormatter';
import {TotalsRowMap} from "./TableList";
import {numberFormatter} from "src/utils/formatters/numberFormatter";
import {stringFormatter} from "src/utils/formatters/stringFormatter";
import {useTranslation} from "react-i18next";
import {styled} from "@mui/material/styles";
import {TableRowSelectionContext} from "./TableWithPaging";
import Icon from "../../@core/components/icon";

export interface ITableColumn {
    hasHTML?: boolean,
    noWrap?: boolean,
    label: string,
    value?: string,
    width?: string | number | undefined,
    size?: "small" | "medium" | undefined
    currency?: string,
    type?: TableColumnType,
    minDecimals?: number,
    maxDecimals?: number,
    textAlign?: 'left' | 'center' | 'right',
    onRenderCell?: (entity: any, ...args: any[]) => React.ReactElement
}

export interface ITableGroups {
    firstColumn: number,
    lastColumn: number,
    label: React.ReactNode,
}

export enum TableColumnType {
    Date = 1,
    Button = 2,
    Boolean = 3,
    Chart = 4,
    Currency = 5,
    Number = 6,
    Decimal = 7,
    CNPJ = 8,
    Percentage = 9,
    Default = 10,
    Warning = 11,
    Datetime = 12,
}

interface ITableRowEntityProps {
    keyComponent: React.Key,
    entity: any,
    columns: ITableColumn[],
    firstRow?: boolean,
}

const StyledTableCell = styled(TableCell)(({theme}) => ({
    '&.MuiTableCell-root': {
        paddingTop: '0px !important',
        paddingBottom: '0px !important',
    },
}));

export function TableRowEntity(props: ITableRowEntityProps) {
    const {selectable, isSelected, handleSelect} = React.useContext(TableRowSelectionContext);


    function getColumnKey(columnIndex: number, value: string | undefined) {
        return `${props.keyComponent}_${columnIndex}_${value}`;
    }

    return (
        <TableRow
            role="checkbox"
            tabIndex={-1}
            selected={(selectable && isSelected(props.entity?.id))}
        >
            {
                selectable
                &&
                <StyledTableCell
                    onClick={(event) => handleSelect(event, props.entity.id)}
                    sx={{pr: '0px !important', pl: '0px !important'}}
                >
                    <Checkbox
                        color="primary"
                        checked={isSelected(props.entity?.id)}
                    />
                </StyledTableCell>
            }
            {
                props.columns.map((oneColumn, index) => {

                    const columnValue: string = (oneColumn.value || "");
                    const textAlign: string = oneColumn.textAlign || "left";

                    const commonSx = { width: oneColumn.width || 'auto', minWidth: oneColumn.width || 'auto'};

                    switch (oneColumn.type) {
                        case TableColumnType.Boolean:
                            return (
                                    <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx} width={oneColumn.width}>
                                    {
                                        (props.entity[columnValue] as boolean) ?

                                            // <CheckCircleOutlineIcon /*className={classes.iconBoolSuccess}*/ /> :
                                            // <RemoveCircleIcon /*className={classes.iconBoolDanger}*/ />

                                            <CheckCircleOutlineIcon sx={{
                                                fill: "#4caf50 !important",
                                                fontSize: "19px !important",
                                            }}/> : <RemoveCircleIcon sx={{
                                                fill: "#f44336 !important",
                                                fontSize: "19px !important",
                                            }}/>
                                    }
                                </StyledTableCell>
                            )

                        case TableColumnType.Warning:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx} width={oneColumn.width}>
                                    {
                                        (props.entity[columnValue] as boolean) ?

                                            <WarningAmberIcon sx={{
                                                fill: (theme) => theme.palette.warning.main,
                                                fontSize: "19px !important",
                                            }}/> : <></>
                                    }
                                </StyledTableCell>
                            )

                        case TableColumnType.Date:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)}
                                                 sx={{textAlign: `${textAlign}`, ...commonSx}}
                                                 width={oneColumn.width}
                                                 >
                                    {dateFormatter.toShortDate(props.entity[columnValue])}
                                </StyledTableCell>
                            )

                        case TableColumnType.Datetime:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)}
                                width={oneColumn.width}
                                                 sx={{textAlign: `${textAlign}`, ...commonSx}}>
                                    {dateFormatter.toShortDateTime(props.entity[columnValue])}
                                </StyledTableCell>
                            )

                        case TableColumnType.Button:
                            return (
                                <StyledTableCell
                                    key={getColumnKey(index, columnValue)} /*className={classes.tableCellButton}*/
                                    sx={{
                                        zoom: "80%",
                                        ...commonSx,
                                    }}
                                    width={oneColumn.width}>
                                    {oneColumn.onRenderCell && oneColumn.onRenderCell(props.entity)}
                                </StyledTableCell>
                            )
                        case TableColumnType.Chart:
                            return (
                                props?.firstRow
                                    ?
                                    <StyledTableCell
                                        align={'center'}
                                        style={{borderBottom: 'none'}} rowSpan={7}
                                        width={oneColumn.width}
                                        >
                                        {
                                            oneColumn.onRenderCell && oneColumn.onRenderCell(props.entity)}
                                    </StyledTableCell>
                                    : <></>

                            )

                        case TableColumnType.Currency:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx}
                                width={oneColumn.width}>
                                    {numberFormatter.toStringWithAmount(props.entity[columnValue], oneColumn.currency || "$",
                                        oneColumn.minDecimals, oneColumn.maxDecimals)}
                                </StyledTableCell>
                            )

                        case TableColumnType.Number:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx}
                                width={oneColumn.width}>
                                    {numberFormatter.toNumberWithoutDecimal(props.entity[columnValue])}
                                </StyledTableCell>
                            )

                        case TableColumnType.Decimal:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx}
                                width={oneColumn.width}>
                                    {numberFormatter.toStringWithAmount(props.entity[columnValue] || 0, '',
                                        oneColumn.minDecimals || 0, oneColumn.maxDecimals)}
                                </StyledTableCell>
                            )

                        case TableColumnType.CNPJ:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx}
                                width={oneColumn.width}>
                                    {stringFormatter.toCNPJ(props.entity[columnValue])}
                                </StyledTableCell>
                            )

                        case TableColumnType.Percentage:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)} sx={commonSx}
                                width={oneColumn.width}>
                                    {numberFormatter.toStringWithPercentage(props.entity[columnValue], oneColumn.minDecimals || 0, oneColumn.maxDecimals)}
                                </StyledTableCell>
                            )

                        default:
                            return (
                                <StyledTableCell key={getColumnKey(index, columnValue)}
                                                 width={oneColumn.width}
                                                 sx={{textAlign: `${textAlign} !important`, ...commonSx}}
                                >
                                    {oneColumn.onRenderCell ? oneColumn.onRenderCell(props.entity) :
                                        props.entity[columnValue]}
                                </StyledTableCell>
                            )
                    }
                })
            }
        </TableRow>
    )
}

interface TableTotalsRowEntityProps<T> {
    totalsRowMapList: TotalsRowMap<T>[],
    entityList: T[] | null | undefined,
    columns: ITableColumn[],
}

function TableTotalsRowEntity<T>(props: TableTotalsRowEntityProps<T>) {
    return (
        <TableRow>
            <TableCell variant={'footer'} style={{borderBottom: 'none'}}><Typography
                sx={{fontWeight: 800}}>TOTAL</Typography></TableCell>
            {
                props.columns.slice(1).map((c, idx) => {
                    const map = props.totalsRowMapList?.find(map => map.columnIndex === idx + 1)
                    const chartColumn = props.columns[idx + 1].type === TableColumnType.Chart
                    let cell = <TableCell
                        variant={'footer'}
                        style={chartColumn ? {border: 'none'} : {borderBottom: 'none'}}
                    />
                    if (chartColumn) {
                        cell = <></>
                    }
                    if (map) {
                        cell =
                            <TableCell variant={'footer'} style={{borderBottom: 'none'}}>
                                {
                                    props.columns[idx + 1]?.onRenderCell
                                        ? React.cloneElement(props.columns[idx + 1].onRenderCell!({
                                            [map.entityField]: props.entityList?.reduce((sum, curr: any) => sum + curr?.[map.entityField], 0)
                                        }), {sx: {color: 'primary.main', fontWeight: 800}})
                                        : props.entityList?.reduce((sum, curr: any) => sum + curr?.[map.entityField], 0)
                                }
                            </TableCell>
                    }

                    return cell
                })
            }
        </TableRow>
    )
}


interface ITableRowHeaderProps {
    columns: ITableColumn[],
}

export function TableRowHeader(props: ITableRowHeaderProps) {
    const {t} = useTranslation();

    const {selectable, handleSelectAllClick, numRows, selected} = React.useContext(TableRowSelectionContext)

    return (
        <TableRow>
            {
                selectable
                &&
                <StyledTableCell

                    sx={{p: '0px !important'}}
                >
                    <Checkbox
                        indeterminate={selected.length > 0 && selected.length < numRows}
                        checked={numRows > 0 && selected.length === numRows}
                        color="primary"
                        onChange={handleSelectAllClick}
                    />
                </StyledTableCell>
            }
            {props.columns.map((oneColumn, index) => {
                const hasHTML = oneColumn.hasHTML || false;
                const whiteSpace = oneColumn.noWrap ? 'nowrap' : 'break-spaces'

                return (
                    <TableCell
                        width={oneColumn.width} size={oneColumn.size}
                        sx={{whiteSpace, pt: 0, pb: 0, minWidth: oneColumn.width}}
                        key={`${oneColumn.value}_${index}`}
                        variant={'head'}>
                        {/*<Typography variant={'body2'} fontWeight={800} color={'primary'}>{oneColumn.label}
                        </Typography>*/}

                        <Typography variant={'subtitle2'}
                                    fontSize={'0.8125rem'}
                                    align={oneColumn.textAlign}>

                            {hasHTML ? <span dangerouslySetInnerHTML={{__html: oneColumn.label}}/> : t(oneColumn.label)}
                        </Typography>
                    </TableCell>
                )
            })}
        </TableRow>
    )
}

interface ITableGroupHeaderRowProps {
    columns: ITableGroups[],
}

export function TableRowGroupHeader(props: ITableGroupHeaderRowProps) {
    const {t} = useTranslation();
    const sortedColumns = [...props.columns].sort((a, b) => a.firstColumn - b.firstColumn)
    const firstGroupColumnIndex = sortedColumns[0].firstColumn

    return (
        <TableRow>
            {

                //Empty cells before first group
                [...Array(firstGroupColumnIndex)].map((_, index) => <TableCell key={index}/>)
            }
            {sortedColumns.map((oneColumn, index) => {

                return (
                    <TableCell

                        key={`$group-header-${index}`} variant={'head'}
                        colSpan={1 + oneColumn.lastColumn - oneColumn.firstColumn}>
                        {/*<Typography variant={'body2'} fontWeight={800} color={'primary'}>{oneColumn.label}
                        </Typography>*/}

                        <Typography variant={'body2'} align={'center'}>
                            {oneColumn.label}
                        </Typography>
                    </TableCell>
                )
            })}
        </TableRow>
    )
}

interface ITableCardHeaderProps {
    title: string,
    subtitle?: string | null,
    onReload?: () => void,
    onToggleShowChildren?: () => void,
    onNewRegister?: () => void,
    childrenName?: string,
    actions?: React.ReactNode,
    onSelectedAction?: (selected: readonly number[]) => void,
    selectedActionName?: string,
    selectedActionIcon?: React.ReactNode,
}

interface CustomCardHeaderProps {
    title: string;
    subheader?: string | null;
    action: React.ReactNode;
}

const CustomCardHeader: React.FC<CustomCardHeaderProps> = ({title, subheader, action}) => {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: {xs: 'column', sm: 'row'},
            justifyContent: 'space-between',
            width: '100%',
            p: 2
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 1
            }}>
                <Box>
                    <Typography variant="h5">{title}</Typography>
                    <Typography variant="subtitle1">{subheader}</Typography>
                </Box>
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row'},
                gap: 2,
                justifyContent: {xs: 'flex-start', sm: 'flex-end'}
            }}>
                {action}
            </Box>
        </Box>
    );
}

export function TableCardHeader(props: ITableCardHeaderProps) {

    const {selectable, selected} = React.useContext(TableRowSelectionContext)

    const [filterExpanded, setFilterExpanded] = useState<boolean>(true)

    const onToggleClicked = () => {
        if (props.onToggleShowChildren) {
            props.onToggleShowChildren()
        }

        return setFilterExpanded(!filterExpanded);
    }

    return (
        <CustomCardHeader

            // sx={{ flexWrap: 'wrap', mt: -2 }}

            title={props.title}

            subheader={props.subtitle}

            action={
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: {xs: 'flex-start', sm: 'flex-end'},
                    gap: 2
                }}>

                    {
                        props.onToggleShowChildren &&
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Icon icon={filterExpanded ? 'tabler:filter-down' : 'tabler:filter-up'}/>}
                            sx={{
                                marginRight: (theme: Theme) => theme.spacing(1)
                            }}
                            onClick={onToggleClicked}>{props.childrenName}</Button>
                    }

                    {
                        props.onNewRegister &&
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon/>}
                            sx={{
                                marginRight: (theme: Theme) => theme.spacing(1)
                            }}
                            onClick={props.onNewRegister}>Nuevo</Button>
                    }

                    {
                        props.onReload &&
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<SyncIcon/>}
                            sx={{
                                marginRight: (theme: Theme) => theme.spacing(1)
                            }}
                            onClick={props.onReload}>Recargar</Button>
                    }

                    {
                        props.onSelectedAction &&
                        <Tooltip title={selected.length === 0 ? 'Seleccione al menos una fila' : ''}>
                            <div>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={selected.length === 0}
                                    onClick={() => props.onSelectedAction && props.onSelectedAction(selected)}
                                    startIcon={props.selectedActionIcon}
                                    sx={{
                                        marginRight: (theme: Theme) => theme.spacing(1)
                                    }}
                                >
                                    {props.selectedActionName || 'Acción'}
                                </Button>
                            </div>
                        </Tooltip>
                    }


                    {props.actions && props.actions}
                </Box>
            }
        />
    )
}


interface ITableBodyEntityListProps<T> {
    entityList: T[] | null | undefined,
    columns: ITableColumn[],
    isLoading: boolean,
    error: boolean,
    totalsRowMapList?: TotalsRowMap<T>[],
    loadingWithSkeleton?: boolean
    noDataMessage?: string | null,
}

export function TableBodyEntityList<T>(props: ITableBodyEntityListProps<T>) {

    return <>{
        props.error ?
            <TableWithError colSpan={props.columns.length}/>
            :
            props.isLoading ?
                props.loadingWithSkeleton ?
                    <TableLoadingSkeleton colSpan={props.columns.length}/>
                    :
                    <TableLoading colSpan={props.columns.length}/>
                :
                props.entityList ?
                    props.entityList.length > 0 ?
                        <>
                            {props.entityList.map((oneEntity, index) => {
                                const keyTableRowEntity = `${(oneEntity as any)["id"]}_${index}`;

                                return (
                                    <TableRowEntity key={keyTableRowEntity}
                                                    keyComponent={keyTableRowEntity}
                                                    entity={(oneEntity as any)}
                                                    columns={props.columns}
                                                    firstRow={index === 0}
                                    />)
                            })}
                            {
                                props?.totalsRowMapList
                                && <TableTotalsRowEntity
                                    columns={props.columns}
                                    entityList={props.entityList}
                                    totalsRowMapList={props.totalsRowMapList}
                                />
                            }
                            {/* si hay un chart se agregan filas vacias a la tabla
                                para mostrar el chart sin que se rompa
                            */}
                            {
                                props.columns.map(e => e.type).includes(TableColumnType.Chart) &&
                                [...Array(Math.max(5 - props.entityList.length, 0))].map((index) =>
                                    <TableRow
                                        key={index}
                                    >
                                        <TableCell
                                            colSpan={props.columns.length}
                                            style={{border: 'none'}}> </TableCell>
                                    </TableRow>
                                )}

                        </>
                        :
                        <TableWithoutData colSpan={props.columns.length} noDataMessage={props.noDataMessage}/>
                    :
                    <TableWithError colSpan={props.columns.length}/>
    }</>
}
